/**
 * @file Hub social en ligne : tant que le joueur est dans le mode hub et que le serveur repond, relie la simulation au salon du serveur —
 * envoie la position du chef (10 Hz), affiche les autres joueurs (rendu seul), fait venir l'herbe a chat du serveur (premier arrive, premier servi).
 * Hors ligne ou serveur injoignable : le hub reste jouable en solo (herbe a chat locale).
 * Chaque joueur diffuse son arme (+ visee, rangee / sortie), son apparence (skin, aura, feu d'artifice, FX de tir) et ses feux d'artifice :
 * voir docs/MULTIJOUEUR.md.
 */
import { CONFIG, HUB_CATNIP } from '@config/index';
import type { AmmoStyle, FireworkStyle } from '@config/index';
import type { Simulation } from '@core/index';
import { DEFAULT_SKIN_ID } from '@config/index';
import type { HubClientEvents } from '@net/HubClient';
import { buildLook, FxGate, lookKey, remoteFireworkStyles, sanitizeFx, sanitizeLook } from '@net/hubLook';
import type { ChatMessage, HubPlayerState, PublicCatLook } from '@net/protocol';
import { ServerHubClient } from '@net/serverAdapters';
import type { HubExtras } from '@net/serverAdapters';
import type { RemoteCat } from '@render/views/EntityViews';
import type { UiBubble, UiChatLine, UiHubPort } from '@ui/UiContext';
import type { OnlineService } from './OnlineService';

/** Frequence d'envoi des positions (s) et delais de reconnexion / nouvelle tentative de cueillette. */
const SEND_INTERVAL = 0.1;
const RECONNECT_DELAY = 6;
const RECONNECT_MAX_DELAY = 60;
const CLAIM_RETRY_DELAY = 1.2;
/** Marge (u) sur le rayon de cueillette cote client : le serveur a la sienne (collectRadius + claimTolerance) et reste juge. */
const CLAIM_MARGIN = 0.15;
/** Verification (s) d'un changement d'apparence locale a renvoyer au salon. */
const LOOK_CHECK_INTERVAL = 1;

export interface RemoteSink {
  setRemoteCats(list: readonly RemoteCat[]): void;
  /** Feu d'artifice d'un autre joueur (le rendu applique le reglage « effets des autres joueurs »). */
  remoteFirework(x: number, y: number, style: AmmoStyle, show: FireworkStyle): void;
}

/** Autre joueur affiche (positions du monde du core) : sert au rendu et aux etiquettes de pseudo. */
export interface HubPeer {
  id: string;
  nickname: string;
  x: number;
  y: number;
}

export type HubLinkState = 'solo' | 'connecting' | 'connected';

/** Emotes du salon (identiques a `emotes` de GameServer/config : le serveur refuse les autres). */
export const HUB_EMOTES = ['wave', 'heart', 'laugh', 'dance', 'sleep', 'angry'] as const;
const CHAT_MAX_LINES = 60;
const CHAT_MAX_CHARS = 120;
const CHAT_COOLDOWN_MS = 800;
const EMOTE_COOLDOWN_MS = 1000;
const BUBBLE_CHAT_MS = 5500;
const BUBBLE_EMOTE_MS = 3200;

export class HubSession implements UiHubPort {
  private hub: ServerHubClient | null = null;
  private state: HubLinkState = 'solo';
  private wasInHub = false;
  private nextTryAt = 0;
  private retryDelay = RECONNECT_DELAY;
  private sendAcc = 0;
  private time = 0;
  private peers: HubPeer[] = [];
  private roomName = '';
  /** Herbe a chat connue du serveur : id -> position. */
  private readonly catnip = new Map<string, { x: number; y: number }>();
  /** Reclamations en attente de reponse / en pause apres un refus : id -> instant (s) de nouvel essai. */
  private readonly claims = new Map<string, number>();
  private readonly listeners = new Set<() => void>();
  /** Apparences connues des autres joueurs (snapshot, `joined`, `look`) ; la derniere recue l'emporte. */
  private readonly looks = new Map<string, PublicCatLook>();
  private readonly fxGate = new FxGate();
  private lastLookKey = '';
  private lookAcc = 0;
  private offFirework: (() => void) | null = null;
  readonly chat: UiChatLine[] = [];
  readonly bubbles = new Map<string, UiBubble>();
  readonly emotes: readonly string[] = HUB_EMOTES;
  unread = 0;
  private readonly blocked = new Set<string>();
  private lastChatAt = 0;
  private lastEmoteAt = 0;
  /** Ami dont on veut rejoindre le salon a la prochaine connexion. */
  private wantFriend: string | null = null;

  constructor(
    private readonly sim: Simulation,
    private readonly online: OnlineService,
    private readonly sink: RemoteSink,
  ) {}

  get link(): HubLinkState {
    return this.state;
  }
  get players(): readonly HubPeer[] {
    return this.peers;
  }
  get room(): string {
    return this.roomName;
  }
  get selfId(): string | null {
    return this.online.userId;
  }

  /** Notifie l'UI a chaque changement d'etat / de liste de joueurs. */
  onChange(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private changed(): void {
    for (const fn of this.listeners) fn();
  }

  private setState(s: HubLinkState): void {
    if (s === this.state) return;
    this.state = s;
    this.changed();
  }

  /** A appeler a chaque frame. */
  update(dt: number): void {
    this.time += dt;
    const inHub = this.sim.getView().mode === 'hub';
    if (inHub !== this.wasInHub) {
      this.wasInHub = inHub;
      if (!inHub) this.leave();
      else this.nextTryAt = 0;
    }
    if (!inHub) return;
    if (this.state === 'solo' && this.online.enabled && this.time >= this.nextTryAt) void this.join();
    if (this.state !== 'connected' || !this.hub) return;

    this.sendAcc += dt;
    if (this.sendAcc >= SEND_INTERVAL) {
      this.sendAcc = 0;
      this.sendPosition();
    }
    this.lookAcc += dt;
    if (this.lookAcc >= LOOK_CHECK_INTERVAL) {
      this.lookAcc = 0;
      const look = this.look();
      const key = lookKey(look);
      if (key !== this.lastLookKey) {
        this.lastLookKey = key;
        this.hub.sendLook(look);
      }
    }
    this.claimNearbyCatnip();
  }

  // ------------------------------------------------------------------------------------------------------------------

  private leader(): { x: number; y: number; rot: number; moving: boolean; aim: number; stowed: boolean } | null {
    const view = this.sim.getView();
    const id = view.catIds[0];
    const e = id === undefined ? undefined : view.entity(id);
    if (!e) return null;
    return { x: e.transform.x, y: e.transform.y, rot: e.transform.rot, moving: e.cat?.moving ?? false, aim: e.cat?.aimAngle ?? e.transform.rot, stowed: view.weaponStowed };
  }

  private look(): PublicCatLook {
    return buildLook(this.sim.data, this.sim.ctx.index, this.sim.data.meta.nickname || (this.online.username ?? ''));
  }

  private sendPosition(): void {
    const l = this.leader();
    if (l && this.hub?.connected) this.hub.sendMove(l.x, l.y, l.rot, l.moving, l.aim, l.stowed);
  }

  /** Mes feux d'artifice partent aussi chez les autres joueurs du salon (le style est reconstruit chez eux d'apres mon apparence). */
  private watchFireworks(): void {
    this.offFirework?.();
    this.offFirework = this.sim.bus.on('Firework', (e) => {
      if (this.state === 'connected' && this.hub?.connected) this.hub.sendFx({ kind: 'firework', x: e.x, y: e.y });
    });
  }

  private setLook(id: string, raw: unknown): PublicCatLook {
    const look = sanitizeLook(raw, CONFIG, DEFAULT_SKIN_ID);
    this.looks.set(id, look);
    return look;
  }

  private async join(): Promise<void> {
    this.setState('connecting');
    this.nextTryAt = this.time + this.retryDelay;
    this.retryDelay = Math.min(RECONNECT_MAX_DELAY, this.retryDelay * 2);
    try {
      if (!(await this.online.retry()) || !this.online.client) throw new Error('hors ligne');
      const friendId = this.wantFriend;
      this.wantFriend = null;
      const hub = new ServerHubClient(this.online.client, this.extras());
      this.hub = hub;
      const events: HubClientEvents = {
        onSnapshot: (players) => this.applySnapshot(players),
        onPlayerLeft: (id) => {
          this.looks.delete(id);
          this.fxGate.forget(id);
          this.peers = this.peers.filter((p) => p.id !== id);
          this.changed();
        },
        onChat: (m) => this.onChat(m),
        onDisconnected: () => {
          if (this.hub !== hub) return;
          this.drop();
        },
      };
      const look = this.look();
      this.lastLookKey = lookKey(look);
      try {
        await hub.connect(this.online.userId ?? '', look, events, friendId ? { friendId } : {});
      } catch (err) {
        if (!friendId) throw err;
        // l'ami n'est pas (ou plus) dans un salon public : on rejoint un salon normal tout de suite
        hub.disconnect();
        this.sim.bus.emit('Toast', { key: 'online.friendNotInHub', kind: 'warning' });
        this.retryDelay = RECONNECT_DELAY;
        this.nextTryAt = this.time;
        this.setState('solo');
        return;
      }
      this.watchFireworks();
      // le salon est rejoint : l'herbe a chat locale cede la place a celle du serveur
      this.sim.dispatch({ type: 'hub.net.online', online: true });
      if (this.sim.getView().mode !== 'hub') {
        this.leave();
        return;
      }
      this.retryDelay = RECONNECT_DELAY;
      this.setState('connected');
    } catch (err) {
      console.warn('hub en ligne indisponible, mode solo', err);
      this.drop();
    }
  }

  private extras(): HubExtras {
    return {
      onJoined: (p) => {
        if (p && typeof p.playerId === 'string') this.setLook(p.playerId, p.look);
      },
      onLook: (id, look) => {
        this.setLook(id, look);
      },
      onFx: (id, raw) => {
        if (id === this.online.userId || this.sim.getView().mode !== 'hub') return;
        const fx = sanitizeFx(raw);
        if (!fx || !this.fxGate.allow(id, this.time * 1000)) return;
        const { style, show } = remoteFireworkStyles(this.looks.get(id), this.sim.ctx.index, CONFIG);
        this.sink.remoteFirework(fx.x, fx.y, style, show);
      },
      onEmote: (id, emote) => {
        this.bubbles.set(id, { text: `emote:${emote}`, until: performance.now() + BUBBLE_EMOTE_MS });
        this.changed();
      },
      onWelcome: (w) => {
        this.sim.dispatch({ type: 'hub.net.online', online: true });
        this.roomName = w.name;
        this.catnip.clear();
        for (const c of w.catnip) this.addCatnip(c.id, c.x, c.y);
        this.changed();
      },
      onCatnip: (ev) => {
        switch (ev.kind) {
          case 'spawn':
            this.addCatnip(ev.id, ev.x, ev.y);
            break;
          case 'taken':
            this.catnip.delete(ev.id);
            this.claims.delete(ev.id);
            this.sim.dispatch({ type: 'hub.net.catnipRemove', id: ev.id });
            break;
          case 'claimed':
            this.catnip.delete(ev.id);
            this.claims.delete(ev.id);
            this.sim.dispatch({ type: 'hub.net.catnipCredit', id: ev.id, gain: ev.gain });
            break;
          case 'missed':
            // trop loin pour le serveur (ou deja prise) : on retentera un peu plus tard tant que la brindille existe
            this.claims.set(ev.id, this.time + CLAIM_RETRY_DELAY);
            break;
        }
      },
    };
  }

  private addCatnip(id: string, x: number, y: number): void {
    this.catnip.set(id, { x, y });
    this.sim.dispatch({ type: 'hub.net.catnipAdd', id, x, y });
  }

  private applySnapshot(players: readonly HubPlayerState[]): void {
    const me = this.online.userId;
    const peers: HubPeer[] = [];
    const cats: RemoteCat[] = [];
    for (const p of players) {
      if (p.playerId === me) continue;
      // un `look` recu apres le snapshot fait foi (serveurs qui relaient sans memoriser) ; sinon celui du snapshot
      const look = this.looks.get(p.playerId) ?? this.setLook(p.playerId, p.look);
      const aim = typeof p.aim === 'number' && Number.isFinite(p.aim) ? p.aim : p.rot;
      cats.push({ id: p.playerId, x: p.x, y: p.y, rot: p.rot, moving: p.moving, skinId: look.skinId, weaponDefId: look.weaponDefId, aim, stowed: p.stowed !== false, auraId: look.auraId ?? null });
      peers.push({ id: p.playerId, nickname: look.nickname, x: p.x, y: p.y });
    }
    this.sink.setRemoteCats(cats);
    const before = this.peers.length;
    this.peers = peers;
    if (peers.length !== before) this.changed();
  }

  /** Reclame les brindilles a portee du chef (une seule demande en vol par brindille). */
  private claimNearbyCatnip(): void {
    const l = this.leader();
    if (!l || !this.hub) return;
    const r = HUB_CATNIP.collectRadius + CLAIM_MARGIN;
    for (const [id, c] of this.catnip) {
      if ((c.x - l.x) ** 2 + (c.y - l.y) ** 2 > r * r) continue;
      const retryAt = this.claims.get(id);
      if (retryAt !== undefined && this.time < retryAt) continue;
      // en attente de la reponse : on reserve la brindille jusqu'a la fin du delai
      this.claims.set(id, this.time + CLAIM_RETRY_DELAY);
      this.hub.claimCatnip(id);
    }
  }

  private clearWorld(): void {
    this.offFirework?.();
    this.offFirework = null;
    this.looks.clear();
    this.bubbles.clear();
    this.fxGate.clear();
    this.catnip.clear();
    this.claims.clear();
    this.sink.setRemoteCats([]);
    if (this.peers.length > 0) {
      this.peers = [];
      this.changed();
    }
  }

  // ------------------------------------------------------------------------------------------------------------------ discussion

  private onChat(m: ChatMessage): void {
    if (this.blocked.has(m.fromId)) return;
    const text = typeof m.text === 'string' ? m.text.slice(0, 300) : '';
    const emote = typeof m.quickEmoteId === 'string' ? m.quickEmoteId : null;
    if (!text && !emote) return;
    const mine = m.fromId === this.online.userId;
    this.chat.push({ id: m.id, fromId: m.fromId, fromName: m.fromName, text, emote, at: typeof m.sentAtMs === 'number' ? m.sentAtMs : Date.now(), mine });
    if (this.chat.length > CHAT_MAX_LINES) this.chat.splice(0, this.chat.length - CHAT_MAX_LINES);
    if (text) this.bubbles.set(m.fromId, { text, until: performance.now() + BUBBLE_CHAT_MS });
    if (!mine) this.unread++;
    this.changed();
  }

  sendChat(text: string): void {
    const clean = text.trim().slice(0, CHAT_MAX_CHARS);
    const now = performance.now();
    if (!clean || !this.hub?.connected || now - this.lastChatAt < CHAT_COOLDOWN_MS) return;
    this.lastChatAt = now;
    this.hub.sendChat('hub', { text: clean });
  }

  sendEmote(id: string): void {
    const now = performance.now();
    if (!this.hub?.connected || !(HUB_EMOTES as readonly string[]).includes(id) || now - this.lastEmoteAt < EMOTE_COOLDOWN_MS) return;
    this.lastEmoteAt = now;
    this.hub.sendEmote(id);
  }

  block(playerId: string): void {
    this.blocked.add(playerId);
    for (let i = this.chat.length - 1; i >= 0; i--) if (this.chat[i]?.fromId === playerId) this.chat.splice(i, 1);
    this.bubbles.delete(playerId);
    this.hub?.sendBlock(playerId);
    this.changed();
  }

  report(playerId: string, reason: string): void {
    this.hub?.sendReport(playerId, reason.slice(0, 200));
    this.sim.bus.emit('Toast', { key: 'online.reportSent', kind: 'info' });
  }

  /** Rejoint le salon d'un ami : entre dans le hub si besoin, puis (re)connecte le salon en visant cet ami. */
  joinFriend(friendId: string): void {
    this.wantFriend = friendId;
    if (this.sim.getView().mode !== 'hub') {
      this.sim.dispatch({ type: 'mode.start', mode: 'hub' });
      return;
    }
    this.rejoin();
  }

  /** Quitte puis rejoint le salon (apres un changement de pseudo, ou pour suivre un ami). */
  rejoin(): void {
    if (this.sim.getView().mode !== 'hub') return;
    this.leave();
    this.nextTryAt = 0;
    this.retryDelay = RECONNECT_DELAY;
  }

  /** Perte de liaison : retour au solo, nouvelle tentative plus tard. */
  private drop(): void {
    this.hub?.disconnect();
    this.hub = null;
    this.clearWorld();
    this.sim.dispatch({ type: 'hub.net.online', online: false });
    this.setState('solo');
  }

  /** Sortie du hub : on quitte le salon. */
  private leave(): void {
    this.hub?.disconnect();
    this.hub = null;
    this.clearWorld();
    this.sim.dispatch({ type: 'hub.net.online', online: false });
    this.roomName = '';
    this.setState('solo');
  }

  dispose(): void {
    this.leave();
    this.listeners.clear();
  }
}
